import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import "./fixLeafletIcons";

/* ----------------- Fit map to all live markers ----------------- */
function FitBounds({ markers, enabled }) {
  const map = useMap();

  useEffect(() => {
    if (!enabled) return;

    const valid = markers.filter(
      m => !isNaN(m.position[0]) && !isNaN(m.position[1])
    );

    if (valid.length > 0) {
      map.fitBounds(valid.map(m => m.position), { padding: [50, 50] });
    }
  }, [markers, enabled]);

  return null;
}

/* ----------------- Zoom to selected worker ----------------- */
function FlyToUser({ user }) {
  const map = useMap();

  useEffect(() => {
    if (user) {
      map.flyTo(user.position, 15, { duration: 2.2 });
    }
  }, [user]);

  return null;
}

export default function LiveDashboard() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [pathData, setPathData] = useState([]); // movement history
  const [loading, setLoading] = useState(true);

  const markerRefs = useRef({});

  const API = process.env.REACT_APP_API_PATH || "http://localhost:8060/api/";

  /* ----------------- Load Latest User Positions ----------------- */
  const loadUsers = async () => {
    try {
      const res = await fetch(`${API}geo/latest`);
      const data = await res.json();

      const formatted = data
        .map(u => ({
          id: u.userid,
          name: u.name,
          mobile: u.mobile,
          position: [
            parseFloat(u.latitude),
            parseFloat(u.longitude)
          ],
          updated: u.captured_at,
        }))
        .filter(u => !isNaN(u.position[0]) && !isNaN(u.position[1]));

      setUsers(formatted);
    } catch (e) {
      console.error("API error:", e);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadUsers();
    const interval = setInterval(loadUsers, 20000);
    return () => clearInterval(interval);
  }, []);

  /* ----------------- Load Movement History of Worker ----------------- */
  const loadHistory = async (userId) => {
    try {
      const res = await fetch(`${API}geo/history/${userId}`);
      const data = await res.json();

      const points = data
        .map(p => [
          parseFloat(p.latitude),
          parseFloat(p.longitude)
        ])
        .filter(p => !isNaN(p[0]) && !isNaN(p[1]));

      setPathData(points);
    } catch (err) {
      console.error("History API Error:", err);
    }
  };

  /* ----------------- Reset to Default View ----------------- */
  const resetView = () => {
    setSelectedUser(null);
    setPathData([]); // clear polyline
  };

  /* ----------------- Auto-open popup ----------------- */
  useEffect(() => {
    if (selectedUser) {
      setTimeout(() => {
        markerRefs.current[selectedUser.id]?.openPopup();
      }, 1400);
    }
  }, [selectedUser]);


  return (
    <div className="flex h-screen">

      {/* ================= LEFT SIDEBAR ================= */}
      <div className="w-72 bg-gray-100 p-4 overflow-auto border-r">
        <h2 className="text-xl font-bold mb-3 flex justify-between">
          Live Users
        </h2>

        {/* Reset Button (visible when a user is selected) */}
        {selectedUser && (
          <button
            onClick={resetView}
            className="mb-4 px-3 py-2 bg-red-500 text-white rounded shadow w-full"
          >
            Reset View
          </button>
        )}

        {loading && <div>Loading...</div>}

        {!loading && users.length === 0 && (
          <div className="text-gray-500 text-sm">No active locations</div>
        )}

        {users.map(u => (
          <div
            key={u.id}
            onClick={() => {
              setSelectedUser(u);
              loadHistory(u.id); 
            }}
            className={`p-3 mb-2 rounded shadow cursor-pointer 
              ${selectedUser?.id === u.id ? "bg-blue-200" : "bg-white hover:bg-gray-200"}`}
          >
            <div className="font-semibold">{u.name}</div>
            <div className="text-sm text-gray-700">{u.mobile}</div>
            <div className="text-xs text-gray-500">Updated: {u.updated}</div>
          </div>
        ))}
      </div>

      {/* ================= MAP ================= */}
      <div className="flex-1">
        <MapContainer
          center={[22.9868, 87.855]}
          zoom={8}
          maxBounds={[[21.5, 85], [24.5, 90]]}
          maxBoundsViscosity={1.0}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

          {/* FlyTo on worker select */}
          <FlyToUser user={selectedUser} />

          {/* Fit markers only when no user is selected */}
          <FitBounds markers={users} enabled={!selectedUser} />

          {/* ---------- Show Polyline ONLY when worker is selected ---------- */}
          {selectedUser && pathData.length > 1 && (
            <Polyline
              positions={pathData}
              pathOptions={{ color: "orange", weight: 5 }}
            />
          )}

          {/* ---------- Clustered Live Markers ---------- */}
          <MarkerClusterGroup chunkedLoading>
            {users.map(u => (
              <Marker
                key={u.id}
                position={u.position}
                ref={el => (markerRefs.current[u.id] = el)}
              >
                <Popup>
                  <b>{u.name}</b><br />
                  {u.mobile}<br />
                  <small>{u.updated}</small>
                </Popup>
              </Marker>
            ))}
          </MarkerClusterGroup>

        </MapContainer>
      </div>
    </div>
  );
}
